import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, RefreshCw, FileSpreadsheet, CalendarClock, ChevronUp, ChevronDown, ChevronsUpDown, ChevronRight, Users as UsersIcon, List, Folder, User as UserIcon } from 'lucide-react';
import { vouchersApi } from '../services/api';
import { useToast } from '../components/Toast/Toast';

type Row = { month: string | null; month_label: string; bill_count: number; party_count: number; receivable: number; payable: number; net: number };
type Totals = { bill_count: number; party_count: number; receivable: number; payable: number; net: number };
type SortKey = 'month' | 'bill_count' | 'party_count' | 'receivable';
type SortDir = 'asc' | 'desc';

// User-wise pivot: rows walk the ledger-group hierarchy, columns are bill months.
type MatrixRow = { type: 'group' | 'ledger'; id: number; name: string; cells: Record<string, number>; total: number; bill_count: number };
type Matrix = {
  months: Array<{ key: string; label: string }>;
  rows: MatrixRow[];
  totals: { cells: Record<string, number>; total: number; bill_count: number };
  group: { id: number; name: string } | null;
  path: Array<{ id: number; name: string }>;
};
const EMPTY_MATRIX: Matrix = { months: [], rows: [], totals: { cells: {}, total: 0, bill_count: 0 }, group: null, path: [] };

const fmt = (n: any) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (n: any) => Number(n || 0).toLocaleString('en-IN');
const toInputDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export default function MonthWiseOutstanding() {
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();
  const [asOf, setAsOf] = useState<string>(toInputDate(new Date()));
  const [search, setSearch] = useState('');
  const [groupId, setGroupId] = useState<number | ''>('');
  const [groups, setGroups] = useState<Array<{ id: number; name: string; parent_id: number | null }>>([]);
  useEffect(() => {
    vouchersApi.getLedgerGroupTree().then(r => { if ((r as any)?.success) setGroups((r as any).data || []); }).catch(() => {});
  }, []);
  // Flatten the group tree into an indented, ordered list for the <select>.
  const flatGroups = useMemo(() => {
    const ids = new Set(groups.map(g => g.id));
    const byParent = new Map<number | null, Array<{ id: number; name: string; parent_id: number | null }>>();
    for (const g of groups) { const k = g.parent_id; if (!byParent.has(k)) byParent.set(k, []); byParent.get(k)!.push(g); }
    Array.from(byParent.values()).forEach(arr => arr.sort((a, b) => a.name.localeCompare(b.name)));
    const out: Array<{ id: number; label: string }> = [];
    const walk = (nodes: any[], depth: number, seen: Set<number>) => {
      for (const n of nodes) {
        if (seen.has(n.id)) continue; seen.add(n.id);
        out.push({ id: n.id, label: `${'  '.repeat(depth)}${depth ? '└ ' : ''}${n.name}` });
        walk(byParent.get(n.id) || [], depth + 1, seen);
      }
    };
    // Roots = no parent OR parent outside the visible (scoped) set — so a
    // group-scoped user's subtree renders even though its top parent is hidden.
    const roots = groups.filter(g => g.parent_id == null || !ids.has(g.parent_id)).sort((a, b) => a.name.localeCompare(b.name));
    walk(roots, 0, new Set());
    return out;
  }, [groups]);
  const [debounced, setDebounced] = useState('');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [totals, setTotals] = useState<Totals>({ bill_count: 0, party_count: 0, receivable: 0, payable: 0, net: 0 });
  const [sortKey, setSortKey] = useState<SortKey>('month');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // ── User-wise view ──
  const [view, setView] = useState<'month' | 'user'>('month');
  const [matrix, setMatrix] = useState<Matrix>(EMPTY_MATRIX);
  const [mLoading, setMLoading] = useState(false);
  // Drill path through the group tree. Empty = root (one row per primary group).
  const [crumbs, setCrumbs] = useState<Array<{ id: number; name: string }>>([]);
  const drillGroupId = crumbs.length ? crumbs[crumbs.length - 1].id : null;

  const fetchMatrix = useCallback(async () => {
    setMLoading(true);
    try {
      const res = await vouchersApi.getUserMonthOutstanding({
        as_of: asOf || undefined,
        search: debounced || undefined,
        side: 'receivable',
        group_id: drillGroupId,
      });
      setMatrix((res.data as Matrix) || EMPTY_MATRIX);
    } catch (e: any) {
      showError('Error', e?.message || 'Failed to load user-wise outstanding');
      setMatrix(EMPTY_MATRIX);
    } finally { setMLoading(false); }
  }, [asOf, debounced, drillGroupId, showError]);

  useEffect(() => { if (view === 'user') fetchMatrix(); }, [view, fetchMatrix]);

  // Drill: a group opens its sub-groups + ledgers; a ledger goes straight to
  // its bill-wise outstanding (the modal on the Ledger Report).
  const openMatrixRow = (r: MatrixRow) => {
    if (r.type === 'group') { setCrumbs(c => [...c, { id: r.id, name: r.name }]); return; }
    // Send an explicit date_from too: the Ledger Report otherwise snaps the
    // range to the latest entry's month on first load, which would move the
    // as-of date the bill-wise modal nets against.
    const d = new Date(asOf);
    const fyStart = `${d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1}-04-01`;
    navigate(`/reports/ledger?ledger_id=${r.id}&bill_wise=1&date_from=${fyStart}&date_to=${encodeURIComponent(asOf)}`);
  };

  const exportMatrix = async () => {
    if (matrix.rows.length === 0) { showError('Export', 'No data to export'); return; }
    const { utils, writeFile } = await import('xlsx');
    const data = matrix.rows.map((r, i) => {
      const row: any = { 'Sr': i + 1, 'Name': r.name, 'Type': r.type === 'group' ? 'Group' : 'Ledger' };
      for (const m of matrix.months) row[m.label] = r.cells[m.key] || 0;
      row['Total'] = r.total;
      return row;
    });
    const totalRow: any = { 'Sr': '', 'Name': 'TOTAL', 'Type': '' };
    for (const m of matrix.months) totalRow[m.label] = matrix.totals.cells[m.key] || 0;
    totalRow['Total'] = matrix.totals.total;
    data.push(totalRow);
    const ws = utils.json_to_sheet(data);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'User-wise Outstanding');
    writeFile(wb, `User_Month_Outstanding_${asOf}.xlsx`);
    showSuccess('Exported', 'Excel downloaded');
  };

  // Drill: open the full Receivables Outstanding report filtered to this month.
  // date_to = the as-of date so the netting (and totals) match this summary.
  const openMonth = (r: Row) => {
    const month = r.month || 'none';
    const grp = groupId ? `&group_id=${groupId}` : '';
    navigate(`/reports/outstanding/receivable?bill_month=${encodeURIComponent(month)}&date_to=${encodeURIComponent(asOf)}${grp}`);
  };

  useEffect(() => { const t = setTimeout(() => setDebounced(search), 300); return () => clearTimeout(t); }, [search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vouchersApi.getMonthWiseOutstanding({ as_of: asOf || undefined, search: debounced || undefined, side: 'receivable', group_id: groupId ? Number(groupId) : undefined });
      setRows(res.data?.rows || []);
      setTotals(res.data?.totals || { bill_count: 0, party_count: 0, receivable: 0, payable: 0, net: 0 });
    } catch (e: any) {
      showError('Error', e?.message || 'Failed to load month-wise outstanding');
    } finally { setLoading(false); }
  }, [asOf, debounced, groupId, showError]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = (a as any)[sortKey], bv = (b as any)[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
    });
  }, [rows, sortKey, sortDir]);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir(k === 'month' ? 'asc' : 'desc'); }
  };
  const SortIcon = ({ col }: { col: SortKey }) =>
    sortKey !== col ? <ChevronsUpDown size={11} className="inline ml-1 text-slate-400" />
      : sortDir === 'asc' ? <ChevronUp size={11} className="inline ml-1 text-blue-600" />
        : <ChevronDown size={11} className="inline ml-1 text-blue-600" />;

  const exportExcel = async () => {
    if (sorted.length === 0) { showError('Export', 'No data to export'); return; }
    const { utils, writeFile } = await import('xlsx');
    const data = sorted.map((r, i) => ({
      'Sr': i + 1, 'Month': r.month_label, 'Bills': r.bill_count, 'Parties': r.party_count,
      'Receivable': r.receivable,
    }));
    data.push({ 'Sr': '' as any, 'Month': 'TOTAL', 'Bills': totals.bill_count, 'Parties': '' as any,
      'Receivable': totals.receivable });
    const ws = utils.json_to_sheet(data);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Month-wise Outstanding');
    writeFile(wb, `Month_Wise_Outstanding_${asOf}.xlsx`);
    showSuccess('Exported', 'Excel downloaded');
  };

  const th = 'px-3 py-2 text-[12px] font-bold text-gray-700 border border-gray-300 bg-slate-100 select-none cursor-pointer hover:text-blue-700';
  const td = 'px-3 py-1.5 text-[13px] text-gray-700 border border-gray-200';

  return (
    <div className="p-3 sm:p-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-blue-600" />
          <h1 className="text-lg font-bold text-gray-800">Month-wise Outstanding</h1>
          <span className="text-[12px] text-gray-400 italic hidden sm:inline">
            {view === 'month' ? 'Open bills by bill month' : 'User × month, drill down to bill-wise'}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* View switch — same data, pivoted two ways. */}
          <div className="flex rounded border border-slate-300 overflow-hidden">
            <button onClick={() => setView('month')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] ${view === 'month' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
              <List size={14} /> Month-wise
            </button>
            <button onClick={() => setView('user')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] border-l border-slate-300 ${view === 'user' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
              <UsersIcon size={14} /> User-wise
            </button>
          </div>
          {view === 'month' && (
            <select value={groupId} onChange={e => setGroupId(e.target.value ? Number(e.target.value) : '')}
              title="Filter by ledger group"
              className="border border-slate-300 rounded px-2 py-1.5 text-[13px] bg-white outline-none focus:ring-1 focus:ring-blue-300 max-w-[200px]">
              <option value="">All Ledger Groups</option>
              {flatGroups.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
            </select>
          )}
          <label className="flex items-center gap-1 text-[12px] text-gray-500">
            As of
            <input type="date" value={asOf} onChange={e => setAsOf(e.target.value)}
              className="border border-slate-300 rounded px-2 py-1.5 text-[13px] outline-none focus:ring-1 focus:ring-blue-300 bg-white" />
          </label>
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search party…"
              className="pl-8 pr-8 py-1.5 border border-slate-300 rounded text-[13px] w-52 outline-none focus:ring-1 focus:ring-blue-300 bg-white" />
            {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X size={13} /></button>}
          </div>
          <button onClick={() => (view === 'month' ? fetchData() : fetchMatrix())} className="p-2 rounded text-slate-500 hover:bg-slate-100" title="Refresh"><RefreshCw size={15} className={(view === 'month' ? loading : mLoading) ? 'animate-spin' : ''} /></button>
          <button onClick={() => (view === 'month' ? exportExcel() : exportMatrix())} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-green-300 rounded text-[13px] text-green-700 bg-green-50 hover:bg-green-100"><FileSpreadsheet size={14} /> Excel</button>
        </div>
      </div>

      {/* Drill breadcrumb (user-wise only) */}
      {view === 'user' && (
        <div className="flex items-center flex-wrap gap-1 mb-2 text-[13px]">
          <button onClick={() => setCrumbs([])}
            className={`px-2 py-1 rounded ${crumbs.length ? 'text-blue-700 hover:bg-blue-50 font-medium' : 'text-slate-500'}`}>All Users</button>
          {crumbs.map((c, i) => (
            <span key={c.id} className="flex items-center gap-1">
              <ChevronRight size={13} className="text-slate-400" />
              <button onClick={() => setCrumbs(cs => cs.slice(0, i + 1))}
                className={`px-2 py-1 rounded ${i === crumbs.length - 1 ? 'text-slate-700 font-semibold' : 'text-blue-700 hover:bg-blue-50 font-medium'}`}>{c.name}</button>
            </span>
          ))}
        </div>
      )}

      {/* ── User-wise matrix ── */}
      {view === 'user' ? (
        <div className="overflow-auto border border-gray-300 rounded bg-white" style={{ maxHeight: 'calc(100vh - 240px)' }}>
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-20">
              <tr>
                <th className={`${th} text-left sticky left-0 z-30 min-w-[220px]`}>Name</th>
                {matrix.months.map(m => (
                  <th key={m.key} className={`${th} text-right whitespace-nowrap min-w-[110px]`}
                    onClick={() => navigate(`/reports/outstanding/receivable?bill_month=${encodeURIComponent(m.key)}&date_to=${encodeURIComponent(asOf)}${drillGroupId ? `&group_id=${drillGroupId}` : ''}`)}
                    title="Open receivables for this month">
                    {m.label}
                  </th>
                ))}
                <th className={`${th} text-right whitespace-nowrap min-w-[120px] bg-slate-200`}>Total</th>
              </tr>
            </thead>
            <tbody>
              {mLoading ? (
                <tr><td colSpan={matrix.months.length + 2} className="px-4 py-10 text-center text-slate-400 text-sm">Loading…</td></tr>
              ) : matrix.rows.length === 0 ? (
                <tr><td colSpan={matrix.months.length + 2} className="px-4 py-10 text-center text-slate-400 text-sm">No outstanding bills found</td></tr>
              ) : matrix.rows.map((r, i) => (
                <tr key={`${r.type}-${r.id}`} className={`${i % 2 ? 'bg-slate-50/60' : 'bg-white'} hover:bg-blue-50/60 cursor-pointer`} onClick={() => openMatrixRow(r)}>
                  <td className={`${td} font-semibold sticky left-0 z-10 ${i % 2 ? 'bg-slate-50' : 'bg-white'}`}>
                    <span className="inline-flex items-center gap-1.5">
                      {r.type === 'group'
                        ? <Folder size={13} className="text-amber-500 shrink-0" />
                        : <UserIcon size={13} className="text-slate-400 shrink-0" />}
                      <span className="text-blue-700 hover:underline decoration-dotted"
                        title={r.type === 'group' ? 'Drill into this group' : 'Open bill-wise outstanding'}>{r.name}</span>
                      <span className="text-[11px] text-slate-400 font-normal">({fmtInt(r.bill_count)})</span>
                    </span>
                  </td>
                  {matrix.months.map(m => {
                    const v = r.cells[m.key] || 0;
                    return (
                      <td key={m.key} className={`${td} text-right tabular-nums ${v < 0 ? 'text-red-600' : 'text-gray-700'}`}>
                        {v ? fmt(v) : <span className="text-slate-300">—</span>}
                      </td>
                    );
                  })}
                  <td className={`${td} text-right tabular-nums font-bold ${r.total < 0 ? 'text-red-700' : 'text-emerald-700'}`}>{fmt(r.total)}</td>
                </tr>
              ))}
            </tbody>
            {matrix.rows.length > 0 && (
              <tfoot>
                <tr className="sticky bottom-0 bg-slate-200 font-bold">
                  <td className={`${td} bg-slate-200 sticky left-0 z-10`}>Total — {matrix.rows.length} row(s)</td>
                  {matrix.months.map(m => (
                    <td key={m.key} className={`${td} bg-slate-200 text-right tabular-nums`}>{matrix.totals.cells[m.key] ? fmt(matrix.totals.cells[m.key]) : '—'}</td>
                  ))}
                  <td className={`${td} bg-slate-300 text-right tabular-nums text-emerald-900`}>{fmt(matrix.totals.total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      ) : (

      /* Table */
      <div className="overflow-auto border border-gray-300 rounded bg-white" style={{ maxHeight: 'calc(100vh - 240px)' }}>
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10">
            <tr>
              <th className={`${th} w-[50px] text-center`}>#</th>
              <th className={`${th} text-left`} onClick={() => toggleSort('month')}>Month<SortIcon col="month" /></th>
              <th className={`${th} text-right`} onClick={() => toggleSort('bill_count')}>Bills<SortIcon col="bill_count" /></th>
              <th className={`${th} text-right`} onClick={() => toggleSort('party_count')}>Parties<SortIcon col="party_count" /></th>
              <th className={`${th} text-right`} onClick={() => toggleSort('receivable')}>Receivable<SortIcon col="receivable" /></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400 text-sm">Loading…</td></tr>
            ) : sorted.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400 text-sm">No outstanding bills found</td></tr>
            ) : sorted.map((r, i) => (
              <tr key={(r.month || 'nd') + i} className={i % 2 ? 'bg-slate-50/60' : 'bg-white'}>
                <td className={`${td} text-center text-slate-400 tabular-nums`}>{i + 1}</td>
                <td className={`${td} font-semibold`}>
                  <button onClick={() => openMonth(r)} className="text-blue-700 hover:underline decoration-dotted text-left" title="Open receivables for this month">{r.month_label}</button>
                </td>
                <td className={`${td} text-right tabular-nums`}>{fmtInt(r.bill_count)}</td>
                <td className={`${td} text-right tabular-nums`}>{fmtInt(r.party_count)}</td>
                <td className={`${td} text-right tabular-nums font-bold text-emerald-700`}>{r.receivable ? fmt(r.receivable) : '—'}</td>
              </tr>
            ))}
          </tbody>
          {sorted.length > 0 && (
            <tfoot>
              <tr className="sticky bottom-0 bg-slate-200 font-bold">
                <td className={`${td} bg-slate-200`} colSpan={2}>Total — {sorted.length} month(s)</td>
                <td className={`${td} bg-slate-200 text-right tabular-nums`}>{fmtInt(totals.bill_count)}</td>
                <td className={`${td} bg-slate-200`}></td>
                <td className={`${td} bg-slate-200 text-right tabular-nums text-emerald-800`}>{fmt(totals.receivable)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      )}
    </div>
  );
}
